import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";

/**
 * "Rename chat" dialog, shared by <AiDrawer /> and the AI Assistant page.
 *
 * The draft title lives here rather than in the caller: every surface that
 * renames a chat wants the same three rules (seed from the current title,
 * Enter confirms, empty is not a title), and keeping them in one place is
 * what stops the two surfaces from drifting apart.
 *
 * The caller owns the persistence: `onConfirm` receives the trimmed title and
 * is expected to call its own hook's renameSession.
 */

const ROUND_FIELD = "20px";
const ROUND_BUTTON = "16px";

/** Matches the backend column and keeps the sidebar rows readable. */
const MAX_TITLE_LENGTH = 80;

interface RenameChatDialogProps {
  open: boolean;
  /** Current title, used to seed the field each time the dialog opens. */
  initialTitle: string;
  onClose: () => void;
  onConfirm: (title: string) => void;
}

const RenameChatDialog: React.FC<RenameChatDialogProps> = ({
  open,
  initialTitle,
  onClose,
  onConfirm,
}) => {
  const translations = useTranslations();
  const t = (key: string, fallback: string) =>
    getTranslation(translations, key) || fallback;

  const [value, setValue] = useState(initialTitle);

  // Re-seed on every open: reusing the previous draft would silently offer
  // one chat's title as the new name of another.
  useEffect(() => {
    if (open) setValue(initialTitle);
  }, [open, initialTitle]);

  const confirm = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: ROUND_FIELD } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {t("ai_drawer.rename_chat_title", "Rinomina chat")}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // The dialog has a single field: Enter means "save", not
              // "submit whatever form happens to be underneath".
              e.preventDefault();
              confirm();
            }
          }}
          placeholder={t(
            "ai_drawer.rename_chat_placeholder",
            "Nuovo nome della chat"
          )}
          inputProps={{ maxLength: MAX_TITLE_LENGTH }}
          InputProps={{ sx: { borderRadius: ROUND_FIELD } }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: ROUND_BUTTON, textTransform: "none" }}
        >
          {t("generic.button.cancel", "Annulla")}
        </Button>
        <Button
          onClick={confirm}
          variant="contained"
          disabled={!value.trim()}
          sx={{ borderRadius: ROUND_BUTTON, textTransform: "none" }}
        >
          {t("generic.button.savechanges", "Salva")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameChatDialog;
